import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { toast } from "sonner";
import {
  PlusIcon, TrashIcon, PencilSimpleIcon, MagnifyingGlassIcon,
  ArrowClockwiseIcon, CheckCircleIcon, XCircleIcon, CurrencyInrIcon,
  ClockClockwiseIcon,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SearchablePicker from "@/components/SearchablePicker";

// Feb 2026 · Supplier Price List — family-keyed. One agreement covers
// every colour/batch variant that shares (Category + Denier + TPM/Roto
// + Item Group / RM Type). Powers PO auto-fill.

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);
const inpCls = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const lblCls = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block";
const btnPrimary = "inline-flex items-center gap-1.5 h-9 px-4 bg-[var(--accent)] text-white text-xs font-mono uppercase tracking-widest rounded-sm hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed";
const btnGhost = "inline-flex items-center gap-1.5 h-9 px-4 border border-[var(--border)] text-xs font-mono uppercase tracking-widest rounded-sm hover:bg-[var(--bg-2)] transition disabled:opacity-40 disabled:cursor-not-allowed";

const empty = () => ({
  id: "", supplier_id: "", family_key: "",
  category: "", denier_count: "", tpm_roto: "", rm_type: "",
  rate: "", uom: "kg", currency: "INR",
  freight_pct: 0, gst_pct: 0, customs_pct: 0,
  valid_from: today(), valid_to: "", notes: "", is_active: true,
  sample_code: "", rm_count: 0,
});

const isActiveOn = (row, day) => {
  if (!row?.is_active) return false;
  if ((row.valid_from || "0000-01-01") > day) return false;
  const vt = row.valid_to || "9999-12-31";
  return vt >= day;
};

export default function SupplierPriceList() {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // History dialog
  const [historyRow, setHistoryRow] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: s }, { data: fam }] = await Promise.all([
        api.get("/supplier-price-list/"),
        api.get("/suppliers/").catch(() => ({ data: [] })),
        api.get("/supplier-price-list/families").catch(() => ({ data: [] })),
      ]);
      setRows(r || []);
      setSuppliers((s || []).filter(x => x.is_active !== false));
      setFamilies(fam || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty()); setDialogOpen(true); };
  const openEdit = (r) => {
    setForm({
      ...empty(), ...r,
      rate: String(r.rate), family_key: r.family_key || "",
    });
    setDialogOpen(true);
  };

  const pickFamily = (fkey) => {
    const f = families.find(x => x.family_key === fkey);
    if (!f) { setForm({ ...form, family_key: "", category: "", denier_count: "", tpm_roto: "", rm_type: "", sample_code: "", rm_count: 0 }); return; }
    setForm({
      ...form,
      family_key: f.family_key,
      category: f.category, denier_count: f.denier_count,
      tpm_roto: f.tpm_roto, rm_type: f.rm_type,
      sample_code: f.sample_code, rm_count: f.rm_count,
    });
  };

  const canSave = form.supplier_id && form.category && Number(form.rate) > 0 && form.valid_from;

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) { toast.error("Supplier, family, rate and Valid From are required"); return; }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/supplier-price-list/${form.id}`, {
          rate: Number(form.rate), uom: form.uom || "", currency: form.currency || "INR",
          valid_from: form.valid_from, valid_to: form.valid_to || "",
          notes: form.notes || "", is_active: form.is_active,
        });
        toast.success("Price agreement updated");
      } else {
        await api.post("/supplier-price-list/", {
          supplier_id: form.supplier_id,
          category: form.category, denier_count: form.denier_count,
          tpm_roto: form.tpm_roto, rm_type: form.rm_type,
          rate: Number(form.rate), uom: form.uom || "kg", currency: form.currency || "INR",
          valid_from: form.valid_from, valid_to: form.valid_to || "",
          notes: form.notes || "",
        });
        toast.success("Price agreement added");
      }
      setDialogOpen(false); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!confirm(`Delete ${r.family_label} @ ${r.supplier_name || r.supplier_id}?`)) return;
    try {
      await api.delete(`/supplier-price-list/${r.id}`);
      toast.success("Removed"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openHistory = async (r) => {
    setHistoryRow(r); setHistoryEntries([]);
    try {
      const { data } = await api.get(`/supplier-price-list/${r.id}/history`);
      setHistoryEntries(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load history"); }
  };

  const day = today();
  const filtered = useMemo(() => {
    let list = rows;
    if (activeOnly) list = list.filter(r => isActiveOn(r, day));
    const n = q.trim().toLowerCase();
    if (!n) return list;
    return list.filter(r =>
      (r.supplier_name || "").toLowerCase().includes(n) ||
      (r.family_label || "").toLowerCase().includes(n) ||
      (r.category || "").toLowerCase().includes(n) ||
      (r.denier_count || "").toLowerCase().includes(n) ||
      (r.tpm_roto || "").toLowerCase().includes(n) ||
      (r.rm_type || "").toLowerCase().includes(n));
  }, [rows, q, activeOnly, day]);

  const activeCount = rows.filter(r => isActiveOn(r, day)).length;
  const pickedFamily = families.find(f => f.family_key === form.family_key);

  return (
    <div className="p-6 space-y-4" data-testid="price-list-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Financial Masters</div>
          <h1 className="font-display text-2xl mt-1">Supplier Price List</h1>
          <div className="text-xs text-zinc-500 mt-1">
            Family-keyed agreements — one rate covers every colour/batch variant that shares
            <span className="font-mono text-[var(--accent)]"> Category · Denier · TPM/Roto · Item Group</span>.
            POs auto-fill from the active row on RM select.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="price-refresh" onClick={load} className={btnGhost} disabled={loading}>
            <ArrowClockwiseIcon size={12} weight="bold" />Refresh
          </button>
          <button data-testid="price-new" onClick={openNew} className={btnPrimary}>
            <PlusIcon size={12} weight="bold" />New Agreement
          </button>
        </div>
      </div>

      <div className="tactical-card p-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest">
          <span className="text-zinc-500">Total · <span className="text-white">{rows.length}</span></span>
          <span className="text-emerald-300">Active Today · {activeCount}</span>
          <span className="text-zinc-500">Families · <span className="text-white">{families.length}</span></span>
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="price-active-only">
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} className="accent-[var(--accent)]" />
            Active only
          </label>
        </div>
        <div className="relative">
          <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="price-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search supplier / family / category…"
            className="h-8 w-80 pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]"
          />
        </div>
      </div>

      <div className="tactical-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Supplier</th>
              <th className="px-3 py-2 text-left">Family</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-left w-24">Denier</th>
              <th className="px-3 py-2 text-left w-20">TPM/Roto</th>
              <th className="px-3 py-2 text-left">Item Group</th>
              <th className="px-3 py-2 text-right w-16">RMs</th>
              <th className="px-3 py-2 text-right w-24">Rate</th>
              <th className="px-3 py-2 text-center w-14">Curr</th>
              <th className="px-3 py-2 text-right w-24">Landed ₹</th>
              <th className="px-3 py-2 text-center w-14">UoM</th>
              <th className="px-3 py-2 text-left w-24">From</th>
              <th className="px-3 py-2 text-left w-24">To</th>
              <th className="px-3 py-2 text-center w-20">Status</th>
              <th className="px-3 py-2 text-right w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={14} className="text-center py-10 text-xs text-zinc-500">
                {rows.length ? "No matches" : 'No price agreements yet — click "New Agreement" to add one'}
              </td></tr>
            )}
            {filtered.map((r, i) => {
              const active = isActiveOn(r, day);
              const addonTotal = (Number(r.freight_pct || 0) + Number(r.gst_pct || 0) + Number(r.customs_pct || 0));
              return (
                <tr key={r.id} data-testid={`price-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                  <td className="px-3 py-2 text-xs">{r.supplier_name || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[var(--accent)]">{r.family_label || "—"}</td>
                  <td className="px-3 py-2 text-[11px] text-zinc-300">{r.category || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{r.denier_count || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{r.tpm_roto || "—"}</td>
                  <td className="px-3 py-2 text-[11px] text-zinc-400">{r.rm_type || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[10px] text-zinc-500">{r.rm_match_count ?? 0}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(r.rate)}</td>
                  <td className="px-3 py-2 text-center text-[10px] font-mono text-zinc-500">{r.currency || "INR"}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300" title={addonTotal ? `+${addonTotal.toFixed(2)}% add-ons` : "no add-ons"}>
                    {fmt(r.landed_rate_inr)}
                    {addonTotal > 0 && <div className="text-[9px] text-zinc-500">+{addonTotal.toFixed(1)}%</div>}
                  </td>
                  <td className="px-3 py-2 text-center text-[10px] font-mono text-zinc-500">{r.uom || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{r.valid_from}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{r.valid_to || <span className="text-zinc-600">∞</span>}</td>
                  <td className="px-3 py-2 text-center">
                    {!r.is_active ? (
                      <span className="text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase bg-zinc-500/15 text-zinc-400 border-zinc-500/40">Inactive</span>
                    ) : active ? (
                      <span className="text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Active</span>
                    ) : (
                      <span className="text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase bg-amber-500/15 text-amber-300 border-amber-500/40">Expired</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <RowActions
                      testid={`price-actions-${i}`}
                      items={[
                        { label: "History", testid: `price-history-${i}`, onClick: () => openHistory(r) },
                        { label: "Edit", testid: `price-edit-${i}`, onClick: () => openEdit(r) },
                        { label: "Delete", tone: "danger", testid: `price-del-${i}`, onClick: () => remove(r) },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="price-dialog" className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" description="Supplier price list action — add, edit or view price history.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CurrencyInrIcon size={16} weight="bold" className="text-[var(--accent)]" />
              {form.id ? "Edit Price Agreement" : "New Price Agreement"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className={lblCls}>Supplier *</label>
              <SearchablePicker
                testid="price-supplier"
                value={form.supplier_id}
                onChange={(v) => setForm({ ...form, supplier_id: v })}
                placeholder="Select supplier…"
                headerHint="Search by supplier code or name"
                disabled={!!form.id}
                options={suppliers.map(s => ({ value: s.id, code: s.code || "", name: s.name }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className={lblCls}>Family (Category · Denier · TPM/Roto · Item Group) *</label>
              <select data-testid="price-family" value={form.family_key} disabled={!!form.id}
                onChange={e => pickFamily(e.target.value)} className={inpCls + " font-mono text-xs disabled:opacity-60"} required>
                <option value="">Select family…</option>
                {families.map(f => (
                  <option key={f.family_key} value={f.family_key}>
                    {f.family_label} · {f.rm_count} RM{f.rm_count === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
              {pickedFamily && (
                <div data-testid="price-family-preview" className="mt-2 text-[10px] font-mono text-zinc-400 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm p-2 space-y-0.5">
                  <div><span className="text-zinc-600">Category · </span>{pickedFamily.category || "—"}</div>
                  <div><span className="text-zinc-600">Denier · </span>{pickedFamily.denier_count || "—"}<span className="text-zinc-600 ml-3">TPM/Roto · </span>{pickedFamily.tpm_roto || "—"}<span className="text-zinc-600 ml-3">Item Group · </span>{pickedFamily.rm_type || "—"}</div>
                  <div className="text-emerald-300">Covers {pickedFamily.rm_count} RM code{pickedFamily.rm_count === 1 ? "" : "s"}
                    {pickedFamily.sample_codes?.length ? <span className="text-zinc-500"> — e.g. {pickedFamily.sample_codes.slice(0, 2).join(", ")}{pickedFamily.rm_count > 2 ? "…" : ""}</span> : null}
                  </div>
                </div>
              )}
              {form.id && (
                <div className="mt-2 text-[10px] font-mono text-zinc-500">Family is locked once created — delete & re-add to change the family.</div>
              )}
            </div>
            <div>
              <label className={lblCls}>Rate (per UoM) *</label>
              <input data-testid="price-rate" type="number" step="0.01" min="0.01" value={form.rate}
                onChange={e => setForm({ ...form, rate: e.target.value })} className={inpCls + " font-mono"} required />
            </div>
            <div>
              <label className={lblCls}>UoM</label>
              <input data-testid="price-uom" value={form.uom}
                onChange={e => setForm({ ...form, uom: e.target.value })} placeholder="kg" className={inpCls + " font-mono"} />
            </div>
            <div>
              <label className={lblCls}>Currency</label>
              <select data-testid="price-currency" value={form.currency}
                onChange={e => setForm({ ...form, currency: e.target.value })} className={inpCls + " font-mono"}>
                {["INR", "USD", "EUR", "GBP", "JPY", "AUD", "CAD"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div />
            <div className="md:col-span-2 border-t border-[var(--border)] pt-2 mt-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Landed-cost Add-ons <span className="normal-case text-zinc-600">(% on top of FX-converted rate)</span></div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={lblCls}>Freight %</label>
                  <input data-testid="price-freight" type="number" step="0.01" min="0" value={form.freight_pct}
                    onChange={e => setForm({ ...form, freight_pct: e.target.value })} className={inpCls + " font-mono"} />
                </div>
                <div>
                  <label className={lblCls}>GST %</label>
                  <input data-testid="price-gst" type="number" step="0.01" min="0" value={form.gst_pct}
                    onChange={e => setForm({ ...form, gst_pct: e.target.value })} className={inpCls + " font-mono"} />
                </div>
                <div>
                  <label className={lblCls}>Customs %</label>
                  <input data-testid="price-customs" type="number" step="0.01" min="0" value={form.customs_pct}
                    onChange={e => setForm({ ...form, customs_pct: e.target.value })} className={inpCls + " font-mono"} />
                </div>
              </div>
              {/* Landed-cost live preview */}
              {Number(form.rate) > 0 && (
                <div data-testid="price-landed-preview" className="mt-2 grid grid-cols-3 gap-3 text-[10px] font-mono bg-[var(--bg-0)] border border-[var(--border)] rounded-sm p-2">
                  <div>
                    <div className="text-zinc-500">Base</div>
                    <div>{Number(form.rate).toFixed(2)} {form.currency}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">× FX{form.currency !== "INR" ? " (live)" : ""}</div>
                    <div>≈ {form.currency === "INR" ? Number(form.rate).toFixed(2) : "converted to INR"}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Landed (₹) = base × FX × (1 + {(Number(form.freight_pct || 0) + Number(form.gst_pct || 0) + Number(form.customs_pct || 0)).toFixed(2)}%)</div>
                    <div className="text-emerald-300">Save to see the exact figure</div>
                  </div>
                </div>
              )}
            </div>
            <div className="md:col-span-2 hidden" />
            <div>
              <label className={lblCls}>Valid From *</label>
              <input data-testid="price-from" type="date" value={form.valid_from}
                onChange={e => setForm({ ...form, valid_from: e.target.value })} className={inpCls + " font-mono"} required />
            </div>
            <div>
              <label className={lblCls}>Valid To <span className="text-zinc-600 normal-case">(blank = open-ended)</span></label>
              <input data-testid="price-to" type="date" value={form.valid_to}
                onChange={e => setForm({ ...form, valid_to: e.target.value })} className={inpCls + " font-mono"} />
            </div>
            <div className="md:col-span-2">
              <label className={lblCls}>Notes</label>
              <textarea data-testid="price-notes" value={form.notes} rows={2}
                onChange={e => setForm({ ...form, notes: e.target.value })} className={inpCls + " h-auto py-2 text-xs"} placeholder="Optional context (contract no, GST terms, MOQ…)" />
            </div>
            {form.id && (
              <div className="md:col-span-2 flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.is_active} data-testid="price-active"
                  onChange={e => setForm({ ...form, is_active: e.target.checked })} className="accent-[var(--accent)]" />
                <span className="text-zinc-400">Agreement is active</span>
              </div>
            )}
          </form>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setDialogOpen(false)} disabled={saving}>
              <XCircleIcon size={12} weight="bold" />Cancel
            </button>
            <button data-testid="price-save" className={btnPrimary} onClick={save} disabled={!canSave || saving}>
              <CheckCircleIcon size={12} weight="bold" />{saving ? "Saving…" : (form.id ? "Update" : "Add Agreement")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyRow} onOpenChange={(o) => { if (!o) { setHistoryRow(null); setHistoryEntries([]); } }}>
        <DialogContent data-testid="price-history-dialog" className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[85vh] overflow-y-auto" description="Supplier price list action — add, edit or view price history.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ClockClockwiseIcon size={16} weight="bold" className="text-[var(--accent)]" />
              History · {historyRow?.family_label} @ {historyRow?.supplier_name}
            </DialogTitle>
          </DialogHeader>
          {historyEntries.length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-6">Loading… or no changes recorded yet</div>
          ) : (
            <div className="space-y-2">
              {historyEntries.map((e, i) => {
                const actionColour = e.action === "created" ? "text-emerald-300" : e.action === "deleted" ? "text-red-300" : "text-amber-300";
                const diffKeys = Object.keys(e.diff || {});
                return (
                  <div key={e.id || i} data-testid={`price-history-entry-${i}`} className="border border-[var(--border)] rounded-sm p-3 text-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono uppercase tracking-widest text-[10px] px-2 py-0.5 rounded-full border ${actionColour} border-current/40`}>{e.action}</span>
                        <span className="font-mono text-[10px] text-zinc-500">{(e.changed_at || "").slice(0, 19).replace("T", " ")}</span>
                      </div>
                      <span className="font-mono text-[10px] text-zinc-500">{e.changed_by || "—"}</span>
                    </div>
                    {diffKeys.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-1">
                        {diffKeys.filter(k => !["updated_at", "created_at"].includes(k)).map(k => (
                          <div key={k} className="text-[10px] font-mono">
                            <span className="text-zinc-500">{k}: </span>
                            <span className="text-red-300 line-through">{JSON.stringify(e.diff[k].before)}</span>
                            <span className="text-zinc-500 mx-1">→</span>
                            <span className="text-emerald-300">{JSON.stringify(e.diff[k].after)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {e.action === "created" && e.after && (
                      <div className="text-[10px] font-mono text-zinc-400 mt-1">
                        rate=<span className="text-white">{e.after.rate}</span> {e.after.currency} · valid {e.after.valid_from} → {e.after.valid_to || "∞"}
                        {(e.after.freight_pct || e.after.gst_pct || e.after.customs_pct) ? (
                          <span> · add-ons {e.after.freight_pct}+{e.after.gst_pct}+{e.after.customs_pct}%</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <button className={btnGhost} onClick={() => { setHistoryRow(null); setHistoryEntries([]); }}>Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
